import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { STATION_QUESTION_TYPES } from '@fieldops/shared'
import { Icon } from '../../components/Icon.js'
import { DialogBody, DialogFooter, DialogHeader, Modal } from '../../components/Modal.js'
import { Choice } from '../../components/repeat/RuleFields.js'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Input,
  ListHead,
  Select,
  SkeletonRows,
  Textarea,
  listRowClass,
} from '../../components/ui.js'
import { cn } from '../../lib/cn.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'

/**
 * Settings → Report forms: the stations a report checks, and the questions
 * asked at each one.
 *
 * Every report form is bespoke and stays in the seed — its questions and the
 * opening of its PDF are written for the one kind of work (decisions 54 and
 * 78). What the office edits here is what changes by customer and by year:
 *
 * - **Station types.** Lockable rat stations, in-ground termite stations,
 *   inspection locations. A station itself belongs to a site and is added by
 *   the technician who finds it there, named from the type's prefix (R1, R2).
 * - **Station checks on a form.** A section that asks the same few questions
 *   at every station of one type at the job's site. The options that mean
 *   activity print red on the PDF and count on its summary.
 * - **Terms**, printed at the end of every report from the form.
 *
 * A change to a form's station checks is a new published version. A report
 * already started keeps the questions it was started with, and a question
 * kept with the same kind of answer keeps its key, so a station's history
 * stays joined up across versions. ADR 029.
 */

type ReportForm = RouterOutputs['reportSettings']['forms'][number]
type FormSection = ReportForm['sections'][number]
type StationType = RouterOutputs['reportSettings']['stationTypes'][number]
type QuestionType = (typeof STATION_QUESTION_TYPES)[number]

const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  SELECT: 'One choice',
  MULTI_SELECT: 'Several choices',
  TEXT: 'Short text',
  NUMBER: 'Number',
  PHOTO: 'Photo',
}

const hasOptions = (type: QuestionType) => type === 'SELECT' || type === 'MULTI_SELECT'

export function ReportFormsPanel() {
  return (
    <>
      <FormsCard />
      <StationTypesCard />
    </>
  )
}

function useInvalidate() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries(trpc.reportSettings.pathFilter())
    // A report opened after this reads the new version.
    void queryClient.invalidateQueries(trpc.forms.pathFilter())
  }
}

/* ------------------------------------------------------------------ */
/* The forms                                                            */
/* ------------------------------------------------------------------ */

type Editing =
  | { kind: 'section'; form: ReportForm; section: FormSection | null }
  | { kind: 'terms'; form: ReportForm }
  | null

function FormsCard() {
  const trpc = useTRPC()
  const forms = useQuery(trpc.reportSettings.forms.queryOptions())
  const types = useQuery(trpc.reportSettings.stationTypes.queryOptions())
  const [editing, setEditing] = useState<Editing>(null)

  return (
    <Card>
      <CardHeader
        title="Report forms"
        description="Each service's report, and the stations it checks at the property."
      />

      {forms.isPending ? (
        <div className="p-[var(--card-pad)]">
          <SkeletonRows rows={4} />
        </div>
      ) : forms.error ? (
        <ErrorState message={errorMessage(forms.error)} onRetry={() => void forms.refetch()} />
      ) : forms.data.length === 0 ? (
        <EmptyState
          title="No report forms"
          description="A form appears here once a service uses it."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-ink-100">
          {forms.data.map((form) => (
            <FormRow
              key={form.id}
              form={form}
              onAddSection={() => setEditing({ kind: 'section', form, section: null })}
              onEditSection={(section) => setEditing({ kind: 'section', form, section })}
              onEditTerms={() => setEditing({ kind: 'terms', form })}
            />
          ))}
        </ul>
      )}

      <Modal
        open={editing?.kind === 'section'}
        onClose={() => setEditing(null)}
        labelledBy="station-section-title"
        className="max-w-3xl"
      >
        {editing?.kind === 'section' ? (
          <StationSectionForm
            form={editing.form}
            section={editing.section}
            types={types.data ?? []}
            allForms={forms.data ?? []}
            onClose={() => setEditing(null)}
          />
        ) : null}
      </Modal>

      <Modal
        open={editing?.kind === 'terms'}
        onClose={() => setEditing(null)}
        labelledBy="form-terms-title"
        className="max-w-2xl"
      >
        {editing?.kind === 'terms' ? (
          <TermsForm form={editing.form} onClose={() => setEditing(null)} />
        ) : null}
      </Modal>
    </Card>
  )
}

function FormRow({
  form,
  onAddSection,
  onEditSection,
  onEditTerms,
}: {
  form: ReportForm
  onAddSection: () => void
  onEditSection: (section: FormSection) => void
  onEditTerms: () => void
}) {
  const trpc = useTRPC()
  const invalidate = useInvalidate()
  const [confirming, setConfirming] = useState<string | null>(null)

  const move = useMutation(trpc.reportSettings.moveStationSection.mutationOptions({ onSuccess: invalidate }))
  const remove = useMutation(
    trpc.reportSettings.removeStationSection.mutationOptions({
      onSuccess: () => {
        setConfirming(null)
        invalidate()
      },
    }),
  )

  const failure = move.error ?? remove.error
  const services =
    form.serviceTypes.length > 0
      ? form.serviceTypes.map((service) => service.name).join(', ')
      : 'No service uses it yet'
  const stationSections = form.sections.filter((section) => section.kind === 'STATIONS')

  return (
    <li aria-labelledby={`form-${form.id}`} className="flex flex-col gap-2 px-[var(--card-pad)] py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 id={`form-${form.id}`} className="text-sm font-semibold text-ink-900">
            {form.name}
          </h3>
          <p className="flex min-w-0 text-xs text-ink-500">
            <span className="truncate" title={services}>
              {services}
            </span>
            <span className="shrink-0 whitespace-pre text-ink-400">
              {' '}
              · version {form.version}
              {form.terms ? '' : ' · no terms'}
            </span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          <Button size="sm" variant="ghost" icon={<Icon name="terms" />} onClick={onEditTerms}>
            Terms
          </Button>
          <Button size="sm" icon={<Icon name="plus" />} onClick={onAddSection}>
            Add station checks
          </Button>
        </div>
      </div>

      <ol aria-label={`${form.name} sections`} className="flex flex-col gap-1">
        {form.sections.map((section, index) =>
          section.kind === 'STATIONS' ? (
            <li
              key={section.key}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-ink-50 px-3 py-[var(--row-pad-y)] ring-1 ring-ink-200 ring-inset"
            >
              <span className="w-4 text-right text-xs tabular-nums text-ink-400">{index + 1}</span>
              <Icon name="grid" className="size-4 text-ink-400" />
              <span className="min-w-0 flex-1">
                <span className="text-[13px] font-medium text-ink-900">{section.name}</span>
                <span className="text-xs text-ink-500">
                  {' '}
                  · each {section.assetType?.name.toLowerCase() ?? 'station'} ·{' '}
                  {section.questions.length === 1 ? 'one question' : `${section.questions.length} questions`}
                </span>
              </span>
              {confirming === section.key ? (
                <span className="flex items-center gap-2">
                  <span className="text-xs text-ink-700">Take it off this form?</span>
                  <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                    Keep
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate({ formId: form.id, sectionKey: section.key })}
                  >
                    Remove
                  </Button>
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <IconButton
                    icon="chevronDown"
                    className="rotate-180"
                    label={`Move ${section.name} up`}
                    disabled={index === 0 || move.isPending}
                    onClick={() => move.mutate({ formId: form.id, sectionKey: section.key, direction: 'up' })}
                  />
                  <IconButton
                    icon="chevronDown"
                    label={`Move ${section.name} down`}
                    disabled={index === form.sections.length - 1 || move.isPending}
                    onClick={() => move.mutate({ formId: form.id, sectionKey: section.key, direction: 'down' })}
                  />
                  <Button size="sm" variant="ghost" onClick={() => onEditSection(section)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirming(section.key)}>
                    Remove
                  </Button>
                </span>
              )}
            </li>
          ) : (
            <li key={section.key} className="flex items-center gap-3 px-3 py-0.5 text-xs text-ink-500">
              <span className="w-4 text-right tabular-nums text-ink-400">{index + 1}</span>
              <span className="truncate">
                {section.name}
                <span className="text-ink-400"> · {section.questionCount} questions</span>
              </span>
            </li>
          ),
        )}
      </ol>

      {stationSections.length === 0 ? (
        <p className="text-xs text-ink-500">
          No station checks. Add them when this report should check bait stations, monitors or
          inspection points at the property.
        </p>
      ) : null}
      {failure ? (
        <p role="alert" className="text-xs text-red-700">
          {errorMessage(failure)}
        </p>
      ) : null}
    </li>
  )
}

function IconButton({
  icon,
  label,
  className,
  disabled,
  onClick,
}: {
  icon: 'chevronDown'
  label: string
  className?: string
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-[var(--control-sm)] place-items-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-30"
    >
      <Icon name={icon} className={cn('size-4', className)} />
    </button>
  )
}

/* ------------------------------------------------------------------ */
/* One station section                                                  */
/* ------------------------------------------------------------------ */

type DraftQuestion = {
  uid: number
  key?: string
  label: string
  shortLabel: string
  type: QuestionType
  /** One option a line, the way the office types a list. */
  options: string
  alertValues: string[]
}

let uid = 0

const draftOf = (question: Omit<DraftQuestion, 'uid' | 'options'> & { options: string[] }): DraftQuestion => ({
  ...question,
  uid: ++uid,
  options: question.options.join('\n'),
})

const optionsOf = (draft: DraftQuestion) =>
  draft.options
    .split('\n')
    .map((option) => option.trim())
    .filter((option, index, all) => option !== '' && all.indexOf(option) === index)

/**
 * The three questions most station checks start from. A starting point, not a
 * template anybody is held to: every word is edited before it is saved.
 */
function starterQuestions(kind: StationType['kind'] | undefined): DraftQuestion[] {
  if (kind === 'LOCATION') {
    return [
      draftOf({ label: 'Inspected', shortLabel: 'Inspected', type: 'SELECT', options: ['Inspected', 'Not accessible'], alertValues: [] }),
      draftOf({ label: 'What was found', shortLabel: 'Found', type: 'SELECT', options: ['Nothing found', 'Activity found'], alertValues: ['Activity found'] }),
      draftOf({ label: 'Photo', shortLabel: '', type: 'PHOTO', options: [], alertValues: [] }),
    ]
  }
  return [
    draftOf({ label: 'Activity', shortLabel: 'Activity', type: 'SELECT', options: ['No activity', 'Activity found'], alertValues: ['Activity found'] }),
    draftOf({ label: 'Bait', shortLabel: 'Bait', type: 'SELECT', options: ['Not needed', 'Topped up', 'Replaced'], alertValues: [] }),
    draftOf({ label: 'Station condition', shortLabel: 'Condition', type: 'SELECT', options: ['Good', 'Damaged', 'Missing', 'Not accessible'], alertValues: ['Damaged', 'Missing'] }),
  ]
}

function StationSectionForm({
  form,
  section,
  types,
  allForms,
  onClose,
}: {
  form: ReportForm
  section: FormSection | null
  types: StationType[]
  allForms: ReportForm[]
  onClose: () => void
}) {
  const trpc = useTRPC()
  const invalidate = useInvalidate()

  const live = types.filter((type) => type.archivedAt === null || type.id === section?.assetType?.id)
  const [name, setName] = useState(section?.name ?? '')
  const [hint, setHint] = useState(section?.hint ?? '')
  const [typeId, setTypeId] = useState(section?.assetType?.id ?? '')
  const [questions, setQuestions] = useState<DraftQuestion[]>(() =>
    section
      ? section.questions.map((question) =>
          draftOf({
            key: question.key,
            label: question.label,
            shortLabel: question.shortLabel ?? '',
            type: (STATION_QUESTION_TYPES as readonly string[]).includes(question.type)
              ? (question.type as QuestionType)
              : 'TEXT',
            options: question.options,
            alertValues: question.alertValues,
          }),
        )
      : [],
  )

  const type = types.find((candidate) => candidate.id === typeId)

  // Another form already asking about this type is the best starting point:
  // the commercial service's rat stations are the same stations a residential
  // treatment checks.
  const elsewhere = allForms
    .flatMap((other) =>
      other.sections
        .filter((candidate) => candidate.kind === 'STATIONS' && candidate.assetType?.id === typeId)
        .filter((candidate) => !(other.id === form.id && candidate.key === section?.key))
        .map((candidate) => ({ form: other, section: candidate })),
    )
    .slice(0, 3)

  const save = useMutation(
    trpc.reportSettings.saveStationSection.mutationOptions({
      onSuccess: () => {
        invalidate()
        onClose()
      },
    }),
  )

  const chooseType = (id: string) => {
    setTypeId(id)
    const chosen = types.find((candidate) => candidate.id === id)
    if (!chosen) return
    if (name.trim() === '') setName(`${chosen.name}s`.replace(/ss$/, 's'))
    if (questions.length === 0) setQuestions(starterQuestions(chosen.kind))
  }

  const update = (at: number, patch: Partial<DraftQuestion>) =>
    setQuestions((current) => current.map((question, index) => (index === at ? { ...question, ...patch } : question)))

  const shift = (at: number, by: -1 | 1) =>
    setQuestions((current) => {
      const to = at + by
      if (to < 0 || to >= current.length) return current
      const next = [...current]
      const [moved] = next.splice(at, 1)
      next.splice(to, 0, moved!)
      return next
    })

  const problems = questions.flatMap((question, index) => {
    const label = question.label.trim() || `Question ${index + 1}`
    if (question.label.trim() === '') return [`Question ${index + 1} needs wording.`]
    if (hasOptions(question.type) && optionsOf(question).length < 2) return [`${label} needs at least two options.`]
    return []
  })
  const ready = name.trim() !== '' && typeId !== '' && questions.length > 0 && problems.length === 0

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!ready) return
    save.mutate({
      formId: form.id,
      ...(section ? { sectionKey: section.key } : {}),
      name: name.trim(),
      hint: hint.trim() || null,
      assetTypeId: typeId,
      questions: questions.map((question) => {
        const options = hasOptions(question.type) ? optionsOf(question) : []
        return {
          ...(question.key ? { key: question.key } : {}),
          label: question.label.trim(),
          shortLabel: question.shortLabel.trim() || null,
          type: question.type,
          options,
          alertValues: question.alertValues.filter((value) => options.includes(value)),
        }
      }),
    })
  }

  return (
    <form className="flex min-h-0 flex-col" onSubmit={submit} noValidate aria-labelledby="station-section-title">
      <DialogHeader
        id="station-section-title"
        title={section ? `Edit ${section.name}` : 'Add station checks'}
        description={`${form.name}. Asked once at every station of the type at the job's property.`}
        onClose={onClose}
      />
      <DialogBody>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Stations of type" htmlFor="station-section-type" required>
            <Select id="station-section-type" value={typeId} onChange={(event) => chooseType(event.target.value)}>
              <option value="">Choose a type…</option>
              {live.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                  {option.prefix ? ` (${option.prefix}1, ${option.prefix}2…)` : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Heading on the report" htmlFor="station-section-name" required>
            <Input
              id="station-section-name"
              value={name}
              placeholder="Bait stations"
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
        </div>
        <Field
          label="A line for the technician"
          htmlFor="station-section-hint"
          hint="Under the heading on the form. Leave it blank if the heading says it."
        >
          <Input
            id="station-section-hint"
            value={hint}
            placeholder="Check every station, including any you add today"
            onChange={(event) => setHint(event.target.value)}
          />
        </Field>

        {elsewhere.length > 0 && !section ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md bg-ink-50 px-3 py-2 text-xs text-ink-700">
            <span>Ask the same questions as</span>
            {elsewhere.map((entry) => (
              <Button
                key={`${entry.form.id}:${entry.section.key}`}
                type="button"
                size="sm"
                onClick={() =>
                  setQuestions(
                    entry.section.questions.map((question) =>
                      draftOf({
                        label: question.label,
                        shortLabel: question.shortLabel ?? '',
                        type: question.type as QuestionType,
                        options: question.options,
                        alertValues: question.alertValues,
                      }),
                    ),
                  )
                }
              >
                {entry.form.name}
              </Button>
            ))}
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-xs font-medium text-ink-700">At each {type?.name.toLowerCase() ?? 'station'}, ask</p>
            <p className="text-[11px] text-ink-500">Tap the options that mean activity. They print red.</p>
          </div>
          {questions.length === 0 ? (
            <p className="rounded-md border border-dashed border-ink-300 px-3 py-4 text-center text-xs text-ink-500">
              Choose a type to start with three questions, or add your own.
            </p>
          ) : null}
          <ol className="flex flex-col gap-2">
            {questions.map((question, index) => (
              <QuestionEditor
                key={question.uid}
                index={index}
                question={question}
                last={index === questions.length - 1}
                onChange={(patch) => update(index, patch)}
                onMove={(by) => shift(index, by)}
                onRemove={() => setQuestions((current) => current.filter((_, at) => at !== index))}
              />
            ))}
          </ol>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            icon={<Icon name="plus" />}
            className="self-start"
            disabled={questions.length >= 12}
            onClick={() =>
              setQuestions((current) => [
                ...current,
                draftOf({ label: '', shortLabel: '', type: 'SELECT', options: [], alertValues: [] }),
              ])
            }
          >
            Add a question
          </Button>
        </div>
      </DialogBody>
      <DialogFooter
        error={save.error ? errorMessage(save.error) : (problems[0] ?? null)}
        hint={`Saving publishes version ${form.version + 1}. Reports already started keep their questions.`}
      >
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={save.isPending} disabled={!ready}>
          {section ? 'Save station checks' : 'Add to the form'}
        </Button>
      </DialogFooter>
    </form>
  )
}

function QuestionEditor({
  index,
  question,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  index: number
  question: DraftQuestion
  last: boolean
  onChange: (patch: Partial<DraftQuestion>) => void
  onMove: (by: -1 | 1) => void
  onRemove: () => void
}) {
  const id = `station-question-${question.uid}`
  const options = optionsOf(question)
  const name = question.label.trim() || `Question ${index + 1}`

  return (
    <li
      aria-label={`Question ${index + 1}`}
      className="flex flex-col gap-2 rounded-lg bg-white p-3 ring-1 ring-ink-200 ring-inset"
    >
      <div className="grid gap-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(150px,1fr)_auto]">
        <Field label="Question" htmlFor={`${id}-label`}>
          <Input
            id={`${id}-label`}
            value={question.label}
            placeholder="How much bait has been eaten?"
            onChange={(event) => onChange({ label: event.target.value })}
          />
        </Field>
        <Field label="Column on the PDF" htmlFor={`${id}-short`}>
          <Input
            id={`${id}-short`}
            value={question.shortLabel}
            placeholder={question.label.split(' ').slice(0, 2).join(' ') || 'Eaten'}
            onChange={(event) => onChange({ shortLabel: event.target.value })}
          />
        </Field>
        <Field label="Answer" htmlFor={`${id}-type`}>
          <Select
            id={`${id}-type`}
            value={question.type}
            onChange={(event) => onChange({ type: event.target.value as QuestionType })}
          >
            {STATION_QUESTION_TYPES.map((type) => (
              <option key={type} value={type}>
                {QUESTION_TYPE_LABEL[type]}
              </option>
            ))}
          </Select>
        </Field>
        <div className="flex items-end gap-0.5">
          <IconButton icon="chevronDown" className="rotate-180" label={`Move ${name} up`} disabled={index === 0} onClick={() => onMove(-1)} />
          <IconButton icon="chevronDown" label={`Move ${name} down`} disabled={last} onClick={() => onMove(1)} />
          <button
            type="button"
            aria-label={`Remove ${name}`}
            title="Remove"
            onClick={onRemove}
            className="grid size-[var(--control-sm)] place-items-center rounded-md text-ink-500 hover:bg-red-50 hover:text-red-700"
          >
            <Icon name="trash" className="size-4" />
          </button>
        </div>
      </div>

      {hasOptions(question.type) ? (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <Field label="Options, one a line" htmlFor={`${id}-options`}>
            <Textarea
              id={`${id}-options`}
              rows={Math.max(3, options.length + 1)}
              value={question.options}
              onChange={(event) => onChange({ options: event.target.value })}
            />
          </Field>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium text-ink-700">Means activity</p>
            {options.length === 0 ? (
              <p className="text-xs text-ink-400">Type the options first.</p>
            ) : (
              <div role="group" aria-label={`${name}: means activity`} className="flex flex-wrap gap-1.5">
                {options.map((option) => {
                  const on = question.alertValues.includes(option)
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        onChange({
                          alertValues: on
                            ? question.alertValues.filter((value) => value !== option)
                            : [...question.alertValues, option],
                        })
                      }
                      className={cn(
                        'min-h-[var(--control-sm)] rounded-full px-3 text-xs font-medium ring-1 ring-inset transition-colors',
                        on
                          ? 'bg-red-50 text-red-800 ring-red-300'
                          : 'bg-white text-ink-600 ring-ink-200 hover:bg-ink-50',
                      )}
                    >
                      {option}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </li>
  )
}

/* ------------------------------------------------------------------ */
/* Terms                                                                */
/* ------------------------------------------------------------------ */

function TermsForm({ form, onClose }: { form: ReportForm; onClose: () => void }) {
  const trpc = useTRPC()
  const invalidate = useInvalidate()
  const [terms, setTerms] = useState(form.terms ?? '')

  const save = useMutation(
    trpc.reportSettings.saveTerms.mutationOptions({
      onSuccess: () => {
        invalidate()
        onClose()
      },
    }),
  )

  return (
    <form
      className="flex min-h-0 flex-col"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate({ formId: form.id, terms: terms.trim() || null })
      }}
    >
      <DialogHeader
        id="form-terms-title"
        title="Terms on the report"
        description={`Printed at the end of every ${form.name.toLowerCase()}. A blank line starts a new paragraph.`}
        onClose={onClose}
      />
      <DialogBody>
        <Field label="Terms" htmlFor="form-terms">
          <Textarea id="form-terms" rows={14} value={terms} onChange={(event) => setTerms(event.target.value)} />
        </Field>
      </DialogBody>
      <DialogFooter
        error={save.error ? errorMessage(save.error) : null}
        hint="Reports already stored keep the terms they were printed with."
      >
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={save.isPending}>
          Save terms
        </Button>
      </DialogFooter>
    </form>
  )
}

/* ------------------------------------------------------------------ */
/* Station types                                                        */
/* ------------------------------------------------------------------ */

function StationTypesCard() {
  const trpc = useTRPC()
  const invalidate = useInvalidate()
  const [showRetired, setShowRetired] = useState(false)
  const [editing, setEditing] = useState<StationType | 'new' | null>(null)

  const types = useQuery(trpc.reportSettings.stationTypes.queryOptions({ includeArchived: showRetired }))
  const archive = useMutation(trpc.reportSettings.archiveStationType.mutationOptions({ onSuccess: invalidate }))

  return (
    <Card>
      <CardHeader
        title="Station and location types"
        action={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowRetired((current) => !current)}>
              {showRetired ? 'Hide retired' : 'Show retired'}
            </Button>
            <Button size="sm" onClick={() => setEditing('new')}>
              Add a type
            </Button>
          </div>
        }
      />
      <p className="max-w-[640px] px-[var(--card-pad)] pt-3 text-xs text-ink-500">
        A station is something left at a property and checked each visit — a bait station, a
        termite station, a fly unit. A location is a place inspected each time, like a roof void.
        Technicians add the stations at each property as they find them, named from the prefix.
      </p>

      {types.isPending ? (
        <div className="p-[var(--card-pad)]">
          <SkeletonRows rows={4} />
        </div>
      ) : types.error ? (
        <ErrorState message={errorMessage(types.error)} onRetry={() => void types.refetch()} />
      ) : types.data.length === 0 ? (
        <EmptyState title="No station types" description="Add the kinds of stations your technicians check." />
      ) : (
        <div className="mt-2">
          <ListHead className="grid-cols-[minmax(0,2fr)_90px_70px_90px_minmax(0,2fr)_auto]">
            <span>Type</span>
            <span>Kind</span>
            <span>Prefix</span>
            <span className="text-right">At sites</span>
            <span>Checked on</span>
            <span className="sr-only">Actions</span>
          </ListHead>
          <ul className="divide-y divide-ink-100">
            {types.data.map((type) => (
              <li
                key={type.id}
                aria-label={type.name}
                className={cn(listRowClass, 'sm:grid-cols-[minmax(0,2fr)_90px_70px_90px_minmax(0,2fr)_auto]')}
              >
                <span className={cn('basis-full truncate text-[13px] font-medium sm:basis-auto', type.archivedAt ? 'text-ink-400' : 'text-ink-900')}>
                  {type.name}
                  {type.archivedAt ? <Badge className="ml-2">Retired</Badge> : null}
                </span>
                <span className="text-xs text-ink-600">{type.kind === 'LOCATION' ? 'Location' : 'Station'}</span>
                <span className="font-mono text-xs text-ink-600">{type.prefix ?? '—'}</span>
                <span className="text-xs tabular-nums text-ink-600 sm:text-right">
                  {type.stations.toLocaleString('en-AU')}
                </span>
                <span className="truncate text-xs text-ink-500" title={type.forms.join(', ')}>
                  {type.forms.length > 0 ? type.forms.join(', ') : 'No form yet'}
                </span>
                <span className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(type)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => archive.mutate({ id: type.id, archived: type.archivedAt === null })}
                  >
                    {type.archivedAt ? 'Restore' : 'Retire'}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {archive.error ? (
        <p role="alert" className="px-[var(--card-pad)] pb-3 text-xs text-red-700">
          {errorMessage(archive.error)}
        </p>
      ) : null}

      <Modal open={editing !== null} onClose={() => setEditing(null)} labelledBy="station-type-title" className="max-w-lg">
        {editing !== null ? (
          <StationTypeForm type={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
        ) : null}
      </Modal>
    </Card>
  )
}

function StationTypeForm({ type, onClose }: { type: StationType | null; onClose: () => void }) {
  const trpc = useTRPC()
  const invalidate = useInvalidate()
  const [name, setName] = useState(type?.name ?? '')
  const [kind, setKind] = useState<'STATION' | 'LOCATION'>(type?.kind === 'LOCATION' ? 'LOCATION' : 'STATION')
  const [prefix, setPrefix] = useState(type?.prefix ?? '')

  const save = useMutation(
    trpc.reportSettings.saveStationType.mutationOptions({
      onSuccess: () => {
        invalidate()
        onClose()
      },
    }),
  )

  return (
    <form
      className="flex min-h-0 flex-col"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        if (name.trim() === '') return
        save.mutate({
          ...(type ? { id: type.id } : {}),
          name: name.trim(),
          kind,
          prefix: prefix.trim() || null,
        })
      }}
    >
      <DialogHeader
        id="station-type-title"
        title={type ? `Edit ${type.name}` : 'Add a station type'}
        description="What technicians add at a property and check on each visit."
        onClose={onClose}
      />
      <DialogBody>
        <Field label="Name" htmlFor="station-type-name" required>
          <Input
            id="station-type-name"
            value={name}
            placeholder="Lockable rat station"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-medium text-ink-700">It is</p>
          <Choice
            label="It is"
            value={kind}
            options={[
              { value: 'STATION', label: 'A station left on site' },
              { value: 'LOCATION', label: 'A place inspected' },
            ]}
            onChange={setKind}
          />
        </div>
        <Field
          label="Prefix"
          htmlFor="station-type-prefix"
          hint={`New ones are numbered from it: ${prefix.trim() || 'R'}1, ${prefix.trim() || 'R'}2.`}
        >
          <Input
            id="station-type-prefix"
            value={prefix}
            maxLength={6}
            className="w-28 font-mono"
            onChange={(event) => setPrefix(event.target.value)}
          />
        </Field>
      </DialogBody>
      <DialogFooter error={save.error ? errorMessage(save.error) : null}>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={save.isPending} disabled={name.trim() === ''}>
          {type ? 'Save' : 'Add the type'}
        </Button>
      </DialogFooter>
    </form>
  )
}
